<template>
  <div class="content-main">
    <div class="content">

      <div v-if="loading" class="loading">Cargando...</div>

      <template v-else-if="reception">
        <!-- Header de página -->
        <div class="page-header">
          <div class="page-header__left">
            <button class="btn-back" @click="$router.push('/receptions')">&larr; Recepciones</button>
            <h2>Recepción #{{ reception.id }}</h2>
            <span :class="['status-badge', `status-${reception.status}`]">
              {{ statusLabel(reception.status) }}
            </span>
          </div>
          <div class="page-header__actions" v-if="reception.status === 'pending'">
            <button class="btn-danger-outline" @click="doDelete" :disabled="actionLoading">
              Eliminar
            </button>
          </div>
        </div>

        <!-- Datos generales -->
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">Doc. Infoauto</span>
            <span class="info-value">{{ reception.infoautoDocumentoId || '-' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Nro. Documento</span>
            <span class="info-value">{{ reception.documentNumber || '-' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Proveedor</span>
            <span class="info-value">{{ reception.supplier || '-' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Almacén</span>
            <span class="info-value">{{ reception.warehouseCode || reception.warehouseId }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Departamento</span>
            <span class="info-value">{{ reception.departamentoId }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Fecha creación</span>
            <span class="info-value">{{ reception.createdAt ? reception.createdAt.substring(0, 10) : '-' }}</span>
          </div>
        </div>

        <!-- Notas de historial -->
        <div class="notes-history" v-if="reception.notes || reception.verifiedNote || reception.confirmedNote">
          <div v-if="reception.notes" class="note-item note-general">
            <span class="note-label">Nota:</span> {{ reception.notes }}
          </div>
          <div v-if="reception.verifiedNote" class="note-item note-verified">
            <span class="note-label">
              Verificada {{ reception.verifiedAt ? '(' + reception.verifiedAt.substring(0,10) + ')' : '' }}:
            </span>
            {{ reception.verifiedNote }}
          </div>
          <div v-if="reception.confirmedNote" class="note-item note-confirmed">
            <span class="note-label">
              Confirmada {{ reception.confirmedAt ? '(' + reception.confirmedAt.substring(0,10) + ')' : '' }}:
            </span>
            {{ reception.confirmedNote }}
          </div>
        </div>

        <!-- Tabla de items -->
        <div class="items-section">
          <h3>Items ({{ reception.items ? reception.items.length : 0 }})</h3>
          <table class="default-table">
            <thead>
              <tr>
                <th>Código NKR</th>
                <th>Producto ID</th>
                <th>Cant. Esperada</th>
                <th>Cant. Recibida</th>
                <th>Ubicación</th>
                <th>Notas</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in reception.items" :key="item.id">
                <td><strong>{{ item.nkrCode }}</strong></td>
                <td>{{ item.productId }}</td>
                <td>{{ item.expectedQuantity }}</td>
                <td>{{ item.receivedQuantity }}</td>
                <td>{{ item.locationName || '-' }}</td>
                <td>{{ item.notes || '' }}</td>
              </tr>
              <tr v-if="!reception.items || reception.items.length === 0">
                <td colspan="6" class="no-data">Sin items.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Acciones de estado -->
        <div class="actions-section">
          <!-- Verificar: pending → verified -->
          <div v-if="reception.status === 'pending'" class="action-block action-block--verify">
            <h4>Marcar como Verificada</h4>
            <p class="action-desc">La recepción ha sido revisada y verificada en el almacén.</p>
            <textarea
              v-model="actionNote"
              rows="3"
              placeholder="Nota de verificación en almacén..."
            ></textarea>
            <button class="btn-warning" @click="doVerify" :disabled="actionLoading">
              {{ actionLoading ? 'Procesando...' : 'Verificar Recepción' }}
            </button>
          </div>

          <!-- Confirmar: verified → confirmed -->
          <div v-if="reception.status === 'verified'" class="action-block action-block--confirm">
            <h4>Marcar como Confirmada</h4>
            <p class="action-desc">La recepción ha sido confirmada en Infoauto.</p>
            <textarea
              v-model="actionNote"
              rows="3"
              placeholder="Nota de confirmación en Infoauto..."
            ></textarea>
            <button class="btn-success" @click="doConfirm" :disabled="actionLoading">
              {{ actionLoading ? 'Procesando...' : 'Confirmar Recepción' }}
            </button>
          </div>

          <div v-if="actionError" class="error-msg">{{ actionError }}</div>
        </div>
      </template>

      <div v-else class="no-data">Recepción no encontrada.</div>
    </div>
  </div>
</template>

<script>
import {
  getReception,
  verifyReception,
  confirmReception,
  deleteReception
} from "@/api/ReceptionsService.js";

export default {
  name: "ReceptionDetailPage",
  data() {
    return {
      reception: null,
      loading: true,
      actionNote: "",
      actionLoading: false,
      actionError: null
    };
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        this.reception = await getReception(this.$route.params.id);
      } catch (e) {
        this.reception = null;
      } finally {
        this.loading = false;
      }
    },
    statusLabel(status) {
      const labels = {
        pending: 'Pendiente',
        verified: 'Verificada',
        confirmed: 'Confirmada',
        draft: 'Borrador',
        completed: 'Completada',
        partial: 'Parcial',
        cancelled: 'Cancelada'
      };
      return labels[status] || status;
    },
    async doVerify() {
      this.actionLoading = true;
      this.actionError = null;
      try {
        this.reception = await verifyReception(this.reception.id, this.actionNote);
        this.actionNote = "";
      } catch (e) {
        this.actionError = "Error al verificar la recepción.";
      } finally {
        this.actionLoading = false;
      }
    },
    async doConfirm() {
      this.actionLoading = true;
      this.actionError = null;
      try {
        this.reception = await confirmReception(this.reception.id, this.actionNote);
        this.actionNote = "";
      } catch (e) {
        this.actionError = "Error al confirmar la recepción.";
      } finally {
        this.actionLoading = false;
      }
    },
    async doDelete() {
      if (!confirm("¿Eliminar esta recepción?")) return;
      this.actionLoading = true;
      try {
        await deleteReception(this.reception.id);
        this.$router.push("/receptions");
      } catch (e) {
        this.actionError = "Error al eliminar.";
        this.actionLoading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/style/css/generic-variables.scss";

.content-main {
  width: 100%;
  height: 100%;
  overflow-y: auto;

  .content {
    padding: 20px 24px;
    max-width: 1100px;
  }
}

.loading, .no-data {
  padding: 40px;
  color: #999;
  text-align: center;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;

  &__left {
    display: flex;
    align-items: center;
    gap: 12px;
    h2 { margin: 0; font-size: 22px; }
  }
}

.btn-back {
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  font-size: 14px;
  padding: 4px 8px;
  border-radius: 4px;
  &:hover { background: #f5f5f5; color: #333; }
}

.status-badge {
  padding: 3px 12px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  white-space: nowrap;
  &.status-pending   { background: #fff3e0; color: #e65100; }
  &.status-verified  { background: #e3f2fd; color: #1565c0; }
  &.status-confirmed { background: #e8f5e9; color: #2e7d32; }
  &.status-draft     { background: #e0e0e0; color: #555; }
  &.status-completed { background: #c8f7c5; color: #2e7d32; }
  &.status-partial   { background: #ffe0b2; color: #e65100; }
  &.status-cancelled { background: #ffcdd2; color: #c62828; }
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
  margin-bottom: 24px;
  padding: 16px;
  background: #fafafa;
  border: 1px solid #e0e0e0;
  border-radius: 6px;

  .info-item {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .info-label {
    font-size: 11px;
    text-transform: uppercase;
    color: #999;
    letter-spacing: 0.5px;
  }
  .info-value {
    font-size: 14px;
    font-weight: 500;
    color: #222;
  }
}

.notes-history {
  margin-bottom: 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;

  .note-item {
    padding: 10px 14px;
    border-radius: 6px;
    font-size: 13px;
    .note-label { font-weight: 600; margin-right: 6px; }
  }
  .note-general  { background: #f5f5f5; }
  .note-verified { background: #e3f2fd; color: #1565c0; }
  .note-confirmed { background: #e8f5e9; color: #2e7d32; }
}

.items-section {
  margin-bottom: 28px;
  overflow-x: auto;

  h3 {
    font-size: 15px;
    font-weight: 600;
    margin-bottom: 10px;
    color: #333;
  }
  table td, table th { white-space: nowrap; }
}

.actions-section {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  border-top: 1px solid #e0e0e0;
  padding-top: 20px;

  .error-msg { color: red; font-size: 13px; width: 100%; }
}

.action-block {
  flex: 1;
  min-width: 300px;
  max-width: 480px;
  padding: 18px;
  border-radius: 6px;

  h4 { margin: 0 0 6px; font-size: 15px; }
  .action-desc { font-size: 13px; color: #666; margin-bottom: 12px; }

  textarea {
    width: 100%;
    padding: 8px 10px;
    border: 1px solid #ccc;
    border-radius: 4px;
    font-size: 13px;
    resize: vertical;
    margin-bottom: 12px;
    box-sizing: border-box;
  }

  &--verify  { background: #fff8e1; border: 1px solid #ffe082; }
  &--confirm { background: #e8f5e9; border: 1px solid #a5d6a7; }
}

.btn-warning {
  background: #fb8c00;
  color: white;
  border: none;
  padding: 9px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
  &:hover:not(:disabled) { background: #e65100; }
}

.btn-success {
  background: #43a047;
  color: white;
  border: none;
  padding: 9px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
  &:hover:not(:disabled) { background: #2e7d32; }
}

.btn-danger-outline {
  background: transparent;
  color: #c62828;
  border: 1px solid #c62828;
  padding: 7px 14px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  &:hover { background: #ffcdd2; }
}
</style>
